import React, { useMemo } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faComments,
  faLightbulb,
  faLocationCrosshairs,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import DbqCommentsPanel from './DbqCommentsPanel';
import DbqSuggestionsPanel from './DbqSuggestionsPanel';

const DbqFieldReviewPanel = ({
  fieldId,
  fieldLabel,
  activeTab = 'comments',
  onTabChange,
  onNavigate,
  onScrollToField,
  reviewFieldIds = [],
  onClose,
  commentCount = 0,
  suggestionCount = 0,
  commentsPanelProps = {},
  suggestionsPanelProps = {},
}) => {
  const currentIndex = reviewFieldIds.indexOf(fieldId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < reviewFieldIds.length - 1 && currentIndex !== -1;
  const showCommentsTab = Boolean(commentsPanelProps?.canComment || commentCount > 0);
  const showSuggestionsTab = Boolean(
    suggestionsPanelProps?.canSuggest ||
      suggestionsPanelProps?.canAcceptSuggestions ||
      suggestionsPanelProps?.canDismissSuggestions ||
      suggestionCount > 0,
  );

  const availableTabs = useMemo(() => {
    return [
      showCommentsTab
        ? {
            key: 'comments',
            label: 'Comments',
            icon: faComments,
            count: commentCount,
          }
        : null,
      showSuggestionsTab
        ? {
            key: 'suggestions',
            label: 'Suggestions',
            icon: faLightbulb,
            count: suggestionCount,
          }
        : null,
    ].filter(Boolean);
  }, [commentCount, showCommentsTab, showSuggestionsTab, suggestionCount]);

  const resolvedTab = availableTabs.some((tab) => tab.key === activeTab)
    ? activeTab
    : (availableTabs[0]?.key || 'comments');

  const handleNavigate = (direction) => {
    if (direction === 'up' && hasPrev) {
      onNavigate?.(reviewFieldIds[currentIndex - 1]);
    } else if (direction === 'down' && hasNext) {
      onNavigate?.(reviewFieldIds[currentIndex + 1]);
    }
  };

  return (
    <div className="dbq-comments-panel dbq-field-review-panel">
      <div className="dbq-comments-header">
        <div className="dbq-comments-title">
          <h4>Field Review</h4>
          <span className="dbq-comments-field-label">{fieldLabel}</span>
        </div>
        <div className="dbq-comments-nav">
          <Button
            variant="link"
            className="dbq-nav-btn"
            onClick={() => handleNavigate('up')}
            disabled={!hasPrev}
            title="Previous field with review items"
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </Button>
          <Button
            variant="link"
            className="dbq-nav-btn"
            onClick={() => handleNavigate('down')}
            disabled={!hasNext}
            title="Next field with review items"
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
          <Button
            variant="link"
            className="dbq-nav-btn"
            onClick={() => onScrollToField?.(fieldId)}
            disabled={!fieldId || typeof onScrollToField !== 'function'}
            title="Scroll to this field"
          >
            <FontAwesomeIcon icon={faLocationCrosshairs} />
          </Button>
          <Button
            variant="link"
            className="dbq-close-btn"
            onClick={onClose}
            title="Close field review"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      </div>

      {availableTabs.length > 1 && (
        <div className="dbq-review-tabs">
          {availableTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`dbq-review-tab ${resolvedTab === tab.key ? 'is-active' : ''}`}
              onClick={() => onTabChange?.(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              <span>{tab.label}</span>
              {tab.count > 0 && <Badge bg="secondary">{tab.count}</Badge>}
            </button>
          ))}
        </div>
      )}

      {resolvedTab === 'suggestions' ? (
        <DbqSuggestionsPanel
          {...suggestionsPanelProps}
          fieldId={fieldId}
          fieldLabel={fieldLabel}
          embedded
        />
      ) : (
        <DbqCommentsPanel
          {...commentsPanelProps}
          fieldId={fieldId}
          fieldLabel={fieldLabel}
          embedded
        />
      )}
    </div>
  );
};

export default DbqFieldReviewPanel;
